import React, { useEffect } from 'react';
import MetaTags from '../components/SEO/MetaTags';
import { testimonials, getAverageRating } from '../data/testimonials';
import {
  StarIcon,
  UserIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
} from '@heroicons/react/24/solid';

const Testimonials = () => {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const stats = [
    { number: '500+', label: 'Happy Clients', icon: UserIcon },
    { number: '95%', label: 'Client Retention', icon: CheckCircleIcon },
    { number: '12+', label: 'Years Experience', icon: BuildingOffice2Icon },
    { number: getAverageRating() + '/5', label: 'Average Rating', icon: StarIcon },
  ];

  return (
    <div>
      <MetaTags
        title="Client Testimonials - Success Stories | NAQD Consulting"
        description="Read success stories from 500+ satisfied clients who trust NAQD Consulting for tax planning, GST compliance, and business advisory services in Kerala."
        keywords="chartered accountant testimonials, client reviews malappuram, business success stories kerala, tax consultant feedback"
      />

      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-naqd-primary to-naqd-primary/80 dark:from-naqd-dark-bg dark:to-naqd-dark-gray">
        <div className="container-custom text-center">
          <div className="animate-on-scroll">
            <h1 className="heading-primary mb-6 text-white">
              Client <span className="text-naqd-green">Success Stories</span>
            </h1>
            <p className="text-xl text-white/90 dark:text-gray-300 max-w-4xl mx-auto">
              Don't just take our word for it. Hear from 500+ satisfied clients
              who've transformed their financial future with NAQD Consulting.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center animate-on-scroll">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-naqd-green/10 rounded-full mb-4">
                  <stat.icon className="w-8 h-8 text-naqd-green" />
                </div>
                <div className="text-3xl font-bold text-naqd-green mb-2">{stat.number}</div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="card animate-on-scroll"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start space-x-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-naqd-green to-green-400 rounded-full flex items-center justify-center">
                    <UserIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{testimonial.name}</h3>
                    <p className="text-sm text-naqd-green">{testimonial.role}</p>
                    <p className="text-xs text-gray-400">{testimonial.company}</p>
                  </div>
                </div>

                <div className="flex items-center mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="w-4 h-4 text-yellow-400" />
                  ))}
                </div>

                <p className="text-gray-300 mb-4 italic">"{testimonial.content}"</p>

                <div className="border-t border-white/10 pt-4">
                  <p className="text-xs text-gray-400">
                    Service: <span className="text-naqd-green">{testimonial.service}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 animate-on-scroll">
              Detailed Case Studies
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto animate-on-scroll">
              In-depth analysis of how we helped clients achieve remarkable results
            </p>
          </div>

          <div className="space-y-8">
            {[{
              title: "MS Traders: 40% Tax Savings Through Strategic Planning",
              description: "How strategic tax planning transformed a trading business's financial outlook",
              result: "₹12 lakhs saved annually",
              duration: "6 months",
              approach: "Comprehensive tax restructuring with digital compliance"
            }, {
              title: "TechStart Solutions: Startup Growth with GST Optimization",
              description: "Helping a tech startup navigate complex GST requirements",
              result: "₹15 lakhs in R&D benefits claimed",
              duration: "3 months",
              approach: "Specialized startup advisory with compliance automation"
            }].map((study, index) => (
              <div key={index} className="card animate-on-scroll">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-2">
                    <h3 className="text-xl font-semibold mb-3">{study.title}</h3>
                    <p className="text-gray-300 mb-4">{study.description}</p>
                    <p className="text-sm text-naqd-green font-medium">{study.approach}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4">
                    <div className="text-2xl font-bold text-naqd-green mb-2">{study.result}</div>
                    <div className="text-sm text-gray-400">{study.duration}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-naqd-green text-white">
        <div className="container-custom text-center">
          <div className="max-w-3xl mx-auto animate-on-scroll">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Join Our Success Stories
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Ready to write your own success story? Contact us today for a free consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="bg-white text-naqd-green font-semibold py-3 px-6 rounded-lg hover:bg-gray-100 transition-colors">
                Get Free Consultation
              </a>
              <a href="tel:+919074286763" className="border-2 border-white text-white font-semibold py-3 px-6 rounded-lg hover:bg-white hover:text-naqd-green transition-colors">
                Call: +91 90742 86763
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;